import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import "./PerformanceChartCard.scss";
import { DatePicker } from "antd";
import { DatePickerIcon } from "../../icons";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;

interface Performance {
  team: string;
  assigned_tickets_count: string;
  closed_tickets_count: string;
}

interface PerformanceChartCardProps {
  performance?: Performance[];
  selectedPeriod?: [Dayjs, Dayjs] | null;
  onPeriodChange?: (range: [Dayjs, Dayjs] | null) => void;
}

const PerformanceChartCard: React.FC<PerformanceChartCardProps> = ({
  performance,
  selectedPeriod,
  onPeriodChange,
}) => {
  const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs] | null>(
    null
  );

  // --- Decide chart data ---
  const hasData = performance && performance.length > 0;
  const chartCategories = hasData ? performance!.map((p) => p.team) : [];
  const assignedSeries = hasData
    ? performance!.map((p) => parseInt(p.assigned_tickets_count || "0", 10))
    : [];
  const closedSeries = hasData
    ? performance!.map((p) => parseInt(p.closed_tickets_count || "0", 10))
    : [];

  const chartSeries =
    hasData || performance?.length === 0
      ? [
          { name: "Assigned", data: assignedSeries },
          { name: "Closed", data: closedSeries },
        ]
      : [];

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 300,
        animateGradually: { enabled: false },
        dynamicAnimation: { enabled: false },
      },
    },
    grid: { borderColor: "#90A4AE", strokeDashArray: 4 },
    plotOptions: {
      bar: {
        columnWidth: "40%",
        borderRadius: 6,
        distributed: false,
        dataLabels: { position: "top" },
      },
    },
    dataLabels: { enabled: true },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: chartCategories,
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: true,
        style: {
          colors: Array(chartCategories.length).fill("#0C3C45"),
          fontSize: "13px",
        },
      },
      tickPlacement: "on",
    },
    yaxis: { labels: { style: { colors: "#2d3b3c" } } },
    fill: { opacity: 1, colors: ["#08363F", "#4A8E9A"] },
    tooltip: { enabled: false },
    legend: {
      position: "bottom",
      horizontalAlign: "left",
      markers: { width: 12, height: 12, radius: 6 },
      labels: { colors: "#2d3b3c" },
    },
    noData: {
      text: "No performance data",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#90A4AE",
        fontSize: "14px",
        fontFamily: "Helvetica, Arial, sans-serif",
      },
    },
  };

  useEffect(() => {
    const endDate = dayjs().endOf("month");
    const startDate = dayjs().subtract(2, "month").startOf("month");
    const defaultRange: [Dayjs, Dayjs] = [startDate, endDate];
    setSelectedRange(defaultRange);

    if (onPeriodChange) onPeriodChange(defaultRange);
  }, [onPeriodChange]);

  const handleRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    setSelectedRange(dates);
    if (onPeriodChange) onPeriodChange(dates);
  };

  return (
    <div className="performance-chart-card">
      <div className="card-header">
        <h3 className="card-title">Performance</h3>
        <div className="card-extra">
          <RangePicker
            picker="month"
            className="month-picker"
            value={selectedRange}
            onChange={handleRangeChange}
            format={(value) => value.format("MMM YYYY")}
            placeholder={["Start month", "End month"]}
            suffixIcon={<DatePickerIcon />}
            allowClear={false}
            disabledDate={(current) =>
              current && current > dayjs().endOf("month")
            }
          />
        </div>
      </div>

      <p className="card-subtitle">Performance of each ticket owner</p>

      <ReactApexChart
        options={chartOptions}
        series={chartSeries}
        type="bar"
        height={211}
        width={520}
      />
    </div>
  );
};

export default PerformanceChartCard;
